import * as React from 'react';
import * as UI from 'core/ui';
import * as Images from 'pos/Images';
import { InvoiceDTO, Permission, Res, SoldProductType, TicketLineDTO } from 'core/api';
import { Command } from 'pos/common';
import { Result, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import * as InvoiceStore from './InvoiceStore';
import { ActionButtonDefinition, PosScreenClassComponent, ScreenSize } from 'pos/ui';
import { InvoiceFacade } from 'pos/facades';
import { Cols, Col, Flex } from 'core/ui';
import classnames from 'classnames';
import * as TicketGift from './InvoiceTicketGift';
import { Message, Modal } from 'core/ui-next/modal';

const CheckBox = ({ id, checked, onChange }: { id: string, checked: boolean, onChange?: () => void }) => {

	const handleOnClick = (event: React.MouseEvent) => {
		// No queremos que se lance el onClick al marcar el checkBox, poque estaríamos ejecutando también el click del padre.
		event.stopPropagation();
	};

	const handleOnChange = (_event: React.ChangeEvent) => onChange?.();

	return <div className='checkbox abc-checkbox' style={{ marginTop: 0 }}>
		<input id={`check_${id}`} type="checkbox" onClick={handleOnClick} onChange={handleOnChange} checked={checked} />
		<label htmlFor={`check_${id}`} ></label>
	</div>;
};



const formatQuantity = UI.Format.asDecimalWith(2);

function buildLine(ticketGiftLine: TicketGift.GiftLine, onClick: (line: TicketLineDTO) => void) {

	const line: TicketLineDTO = ticketGiftLine.line;

	const isMenuItem = line.Product.Type === SoldProductType.MenuItem;

	const saleFormatName = <span>{line.Product.SaleFormatName}</span>;
	const addins = line.Addins.map((ad, index) => <span key={index}><br />{Res.Web.TICKET_LINES_CONTROL_PRODUCT_WITH_LBL()} {ad.ProductName} </span>);
	const lots = [line.Product.Lot, ...line.Addins.map(x => x.Lot)].filter(x => x.Id > 0).map((lot, index) => <span key={index}><br />{Res.Web.TICKET_LINES_CONTROL_PRODUCT_LOT_LBL(lot.Number)}</span>);
	const removedIngredients = line.RemovedIngredients.map((re, index) => <span key={index}><br />{Res.Web.TICKET_LINES_CONTROL_PRODUCT_WITHOUT_LBL()} {re.SaleFormatName}</span>);
	const onLineClick = () => onClick(ticketGiftLine.line);

	return <li key={line.Id} className={classnames({ 'text-muted': isMenuItem })} onClick={onLineClick}>
		<Cols>
			<Col width='7ex' className='text-right'>
				{isMenuItem ? null : <CheckBox id={`check_${line.Id}`} checked={ticketGiftLine.selected} />}
			</Col>
			<Col width='7ex' className='text-right'>
				{formatQuantity(line.Quantity)}
			</Col>
			<Col width='*' className='text-left m-l-xs'>
				{saleFormatName}
				{addins}
				{removedIngredients}
				{lots}
			</Col>
			<Col width='7ex'>
				{isMenuItem ? Res.Web.TICKET_LINES_CONTROL_MENU_ITEM_LBL() : ''}
			</Col>
		</Cols>
	</li>;
}


type Props = {
	lines: TicketLineDTO[],
} & Modal.Actions<TicketLineDTO[]>;


type State = TicketGift.GiftSelection;
class GetGiftLinesScreen extends PosScreenClassComponent<Props, State> {

	createInitialState(props: Props): Nullable<State> {
		return TicketGift.init(props.lines);
	}

	protected getScreenId(): string {
		return 'GET_TICKET_GIFT_LINES';
	}

	protected getTitle(): string {
		return 'Ticket Regalo';
	}

	protected getImage(): string | SvgIcon {
		return Images.TicketGift;
	}

	protected getSize(): ScreenSize {
		return 'medium';
	}

	protected getActionButtonsDefs(): Nullable<ActionButtonDefinition>[] {

		const onOk = async () => {

			const candidateLines = this.state.lines.filter(x => x.selected).map(x => x.line);
			if (candidateLines.length === 0) {
				await Message.show('Debe seleccionar al menos una línea');
				return;
			}

			this.close(candidateLines);
		};

		return [
			this.cancelButton(async () => this.cancel()),
			null,
			null,
			null,
			null,
			this.okButton(onOk)
		];
	}

	protected getContent(): React.ReactNode {

		const onLineClick = (l: TicketLineDTO) => {
			this.setState(TicketGift.toggleLineSelection(this.state, l));
		};

		const onToggleAllSelection = (): void => {
			const areAllChecked = !this.state.areAllChecked;
			this.setState(areAllChecked ? TicketGift.selectAll(this.state) : TicketGift.unSelectAll(this.state));
		};

		return <Flex.Vertical className='ticket-lines'>
			<Flex.Item>
				<Cols className='list-header'>
					<Col width='7ex' className='text-right'>
						<CheckBox id={'check_all_ticket_gift_lines'} checked={this.state.areAllChecked} onChange={onToggleAllSelection} />
					</Col>
					<Col width='7ex' className='text-right'>
						{Res.Web.TICKET_CONTROL_QTY()}
					</Col>
					<Col width='*' className='text-left m-l-xs'>
						{Res.Web.TICKET_CONTROL_PRODUCT()}
					</Col>
				</Cols>
			</Flex.Item>

			<Flex.Item size='*'>
				<ul className='list'>
					{this.state.lines.map(line => buildLine(line, onLineClick))}
				</ul>
			</Flex.Item>
		</Flex.Vertical>;
	}

	onComponentDidMount(): void {
	}

	onComponentWillUnmount(): void {
	}

	protected useKeypad() {
		return false;
	}
}


declare global {
	interface CommandType {
		printInvoiceTicketGift: (invoice: InvoiceDTO) => Promise<Result>
	}
}

Command.register('printInvoiceTicketGift', Preconditions.ensure(Permission.Ticket_PrintGift), async (invoice: InvoiceDTO): Promise<Result> => {

	if (!SessionProvider.getCurrent().AllowPrintTicketGift) {
		await Message.show(Res.Msg.MSG_TPV_IS_NOT_ALLOWED_TO_PRINT_TICKET_GIFT());
		return Result.error();
	}

	const lines: TicketLineDTO[] = await InvoiceFacade.getLines(invoice.Id);
	const result = await Modal.show(GetGiftLinesScreen, { lines: lines });
	if (!result.ok)
		return Result.error();

	await InvoiceStore.printGift(invoice.Id, result.value);
	return Result.ok();
});

